/**
 * A single task rendered as a bar on the dispatch board grid.
 *
 * The parent positions us via CSS grid (column-start / span). We render
 * the visual block with status color and task type accent.
 */
import {
  AlertTriangle,
  Hammer,
  Package,
  Truck,
  Wrench,
} from 'lucide-react';
import type { DispatchTask, TaskType } from '@vari-crew/schemas';
import { cn } from '@/lib/cn';
import { formatDateTime } from '@/lib/format';

const TYPE_ICON: Record<TaskType, typeof Truck> = {
  pickup: Truck,
  install: Hammer,
  punch: Wrench,
  labor: Package,
};

const STATUS_CLASSES: Record<DispatchTask['status'], string> = {
  scheduled: 'bg-status-scheduled text-status-scheduled-fg border-status-scheduled-fg/20',
  en_route: 'bg-status-enroute text-status-enroute-fg border-status-enroute-fg/30',
  on_site: 'bg-status-onsite text-status-onsite-fg border-status-onsite-fg/30',
  in_progress: 'bg-status-onsite text-status-onsite-fg border-status-onsite-fg/40',
  paused: 'bg-status-scheduled text-status-scheduled-fg border-status-scheduled-fg/30',
  complete: 'bg-status-complete text-status-complete-fg border-status-complete-fg/30',
  blocked: 'bg-status-blocked text-status-blocked-fg border-status-blocked-fg/40',
  cancelled: 'bg-fill-soft text-ink-faint border-line line-through',
};

export function TaskBar({
  task,
  unassigned = false,
  withMeta = '',
  style,
}: {
  task: DispatchTask;
  unassigned?: boolean;
  withMeta?: string;
  style?: React.CSSProperties;
}) {
  const Icon = TYPE_ICON[task.type];
  const subtypeLabel =
    task.type === 'pickup'
      ? task.pickup_subtype
      : task.type === 'install'
        ? task.install_subtype
        : null;
  const time =
    task.scheduled_start && task.scheduled_end
      ? `${formatDateTime(task.scheduled_start).split('· ')[1]} – ${formatDateTime(task.scheduled_end).split('· ')[1]}`
      : '';

  // Presentational only — the parent (DraggableTaskBar) owns click (open the
  // assign picker) vs drag (reassign). No navigation here: a click used to fall
  // through a <Link> and yank the supervisor to the project mid-drag.
  return (
    <div
      style={style}
      className={cn(
        'block px-2 py-1.5 rounded-md border text-xs leading-tight overflow-hidden select-none',
        'hover:shadow-sm transition-shadow',
        STATUS_CLASSES[task.status],
        unassigned && 'border-dashed opacity-80',
      )}
      title={`${task.title}${time ? ' · ' + time : ''}`}
    >
      <div className="flex items-center gap-1 font-semibold truncate">
        <Icon className="w-3 h-3 flex-shrink-0 opacity-70" />
        <span className="truncate">{task.title}</span>
      </div>
      <div className="text-[10px] opacity-80 truncate">
        {task.project_client_name}
        {subtypeLabel && ` · ${subtypeLabel}`}
        {(task.vehicle_label ?? task.truck_label) &&
          ` · ${task.vehicle_label ?? task.truck_label}`}
        {task.crew_size_needed > 1 && ` · ${task.crew_size_needed} crew`}
        {task.status === 'blocked' && (
          <span className="inline-flex items-center gap-0.5 ml-1">
            <AlertTriangle className="w-2.5 h-2.5" /> blocked
          </span>
        )}
        {withMeta && ` · ${withMeta}`}
      </div>
    </div>
  );
}
